import React, { useEffect, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";
import AdminNavbar from "../components/AdminNavbar";
import AdminAPI from "../../services/adminApi";
import { toast } from "react-toastify";

function AdminRecommendations() {
  const [recommendations, setRecommendations] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [farmers, setFarmers] = useState([]);
  const [selectedRecommendation, setSelectedRecommendation] =
    useState(null);

  const [filters, setFilters] = useState({
    search: "",
    priority: "",
    type: "",
    cycle: ""
  });

  const [form, setForm] = useState({
    farmer_id: "",
    cycle_id: "",
    recommendation_type: "",
    recommendation_text: "",
    priority: "medium"
  });

  useEffect(() => {
    fetchRecommendations();
    fetchAnalytics();
    fetchFarmers();
  }, []);

  const fetchRecommendations = async () => {
    try {
      const res = await AdminAPI.get(
        `/recommendations?search=${filters.search}&priority=${filters.priority}&type=${filters.type}&cycle=${filters.cycle}`
      );
      setRecommendations(res.data);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load recommendations");
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await AdminAPI.get(
        "/recommendations/analytics/summary"
      );
      setAnalytics(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchFarmers = async () => {
    try {
      const res = await AdminAPI.get("/farmers");
      setFarmers(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleFormChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const createRecommendation = async (e) => {
    e.preventDefault();

    try {
      await AdminAPI.post("/recommendations", form);

      toast.success("Recommendation created");

      setForm({
        farmer_id: "",
        cycle_id: "",
        recommendation_type: "",
        recommendation_text: "",
        priority: "medium"
      });

      fetchRecommendations();
      fetchAnalytics();
    } catch (err) {
      console.log(err);
      toast.error("Failed to create recommendation");
    }
  };

  const openRecommendation = async (id) => {
    try {
      const res = await AdminAPI.get(
        `/recommendations/${id}`
      );

      setSelectedRecommendation(res.data);

      setForm({
        farmer_id: res.data.farmer_id,
        cycle_id: res.data.cycle_id || "",
        recommendation_type:
          res.data.recommendation_type || "",
        recommendation_text:
          res.data.recommendation_text || "",
        priority: res.data.priority || "medium"
      });

    } catch {
      toast.error("Failed to load recommendation");
    }
  };

  const updateRecommendation = async () => {
    try {
      await AdminAPI.put(
        `/recommendations/${selectedRecommendation.id}`,
        form
      );

      toast.success("Recommendation updated");

      setSelectedRecommendation(null);

      fetchRecommendations();
      fetchAnalytics();

    } catch {
      toast.error("Update failed");
    }
  };

  const deleteRecommendation = async (id) => {
    if (!window.confirm("Delete recommendation?")) return;

    try {
      await AdminAPI.delete(
        `/recommendations/${id}`
      );

      toast.success("Recommendation deleted");

      fetchRecommendations();
      fetchAnalytics();

    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="d-flex">
      <AdminSidebar />

      <div
        style={{
          flex: 1,
          background: "#f5f7fa",
          minHeight: "100vh",
          padding: "20px"
        }}
      >
        <AdminNavbar />

        <h2 className="mt-3">
          🤖 AI Recommendations
        </h2>

        {/* Analytics */}
        {analytics && (
          <div className="row mt-4 g-3">
            <div className="col-md-3">
              <div className="card shadow border-0">
                <div className="card-body">
                  <h6>Total</h6>
                  <h3>{analytics.total_recommendations}</h3>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="card shadow border-0">
                <div className="card-body">
                  <h6>High Priority</h6>
                  <h3>{analytics.high_priority}</h3>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="card shadow border-0">
                <div className="card-body">
                  <h6>Medium Priority</h6>
                  <h3>{analytics.medium_priority}</h3>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="card shadow border-0">
                <div className="card-body">
                  <h6>Low Priority</h6>
                  <h3>{analytics.low_priority}</h3>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create */}
        <div className="card shadow border-0 mt-4">
          <div className="card-body">
            <h4>Create Recommendation</h4>

            <form
              onSubmit={createRecommendation}
              className="row g-3 mt-2"
            >
              <div className="col-md-3">
                <select
                  name="farmer_id"
                  className="form-select"
                  value={form.farmer_id}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">
                    Select Farmer
                  </option>

                  {farmers.map((farmer) => (
                    <option
                      key={farmer.id}
                      value={farmer.id}
                    >
                      {farmer.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-2">
                <input
                  name="cycle_id"
                  className="form-control"
                  placeholder="Cycle ID"
                  value={form.cycle_id}
                  onChange={handleFormChange}
                />
              </div>

              <div className="col-md-3">
                <input
                  name="recommendation_type"
                  className="form-control"
                  placeholder="Type"
                  value={form.recommendation_type}
                  onChange={handleFormChange}
                />
              </div>

              <div className="col-md-2">
                <select
                  name="priority"
                  className="form-select"
                  value={form.priority}
                  onChange={handleFormChange}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="col-12">
                <textarea
                  name="recommendation_text"
                  rows="3"
                  className="form-control"
                  placeholder="Recommendation text"
                  value={form.recommendation_text}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="col-12">
                <button className="btn btn-success">
                  Create Recommendation
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Filters */}
        <div className="card shadow border-0 mt-4">
          <div className="card-body">
            <div className="d-flex gap-3 flex-wrap">
              <input
                type="text"
                name="search"
                className="form-control"
                style={{ width: "220px" }}
                placeholder="Search farmer"
                value={filters.search}
                onChange={handleFilterChange}
              />

              <select
                name="priority"
                className="form-select"
                style={{ width: "180px" }}
                value={filters.priority}
                onChange={handleFilterChange}
              >
                <option value="">All Priority</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              <input
                type="text"
                name="type"
                className="form-control"
                style={{ width: "220px" }}
                placeholder="Recommendation type"
                value={filters.type}
                onChange={handleFilterChange}
              />

              <input
                type="text"
                name="cycle"
                className="form-control"
                style={{ width: "180px" }}
                placeholder="Cycle ID"
                value={filters.cycle}
                onChange={handleFilterChange}
              />

              <button
                className="btn btn-success"
                onClick={fetchRecommendations}
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card shadow border-0 mt-4">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-success">
                  <tr>
                    <th>Farmer</th>
                    <th>Cycle</th>
                    <th>Type</th>
                    <th>Priority</th>
                    <th>Text</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {recommendations.map((item) => (
                    <tr key={item.id}>
                      <td>{item.full_name}</td>
                      <td>{item.cycle_id}</td>
                      <td>{item.recommendation_type}</td>
                      <td>{item.priority}</td>
                      <td>
                        {item.recommendation_text}
                      </td>
                      <td>{item.created_at}</td>

                      <td>
                        <button
                          className="btn btn-primary btn-sm me-2"
                          onClick={() =>
                            openRecommendation(item.id)
                          }
                        >
                          Edit
                        </button>

                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() =>
                            deleteRecommendation(item.id)
                          }
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>

              </table>
            </div>
          </div>
        </div>

        {/* Modal */}
        {selectedRecommendation && (
          <div
            className="modal d-block"
            style={{
              background: "rgba(0,0,0,0.6)"
            }}
          >
            <div className="modal-dialog">
              <div className="modal-content">

                <div className="modal-header">
                  <h5>Edit Recommendation</h5>

                  <button
                    className="btn-close"
                    onClick={() =>
                      setSelectedRecommendation(null)
                    }
                  />
                </div>

                <div className="modal-body">
                  <input
                    name="cycle_id"
                    className="form-control mb-3"
                    placeholder="Cycle ID"
                    value={form.cycle_id}
                    onChange={handleFormChange}
                  />

                  <input
                    name="recommendation_type"
                    className="form-control mb-3"
                    placeholder="Type"
                    value={form.recommendation_type}
                    onChange={handleFormChange}
                  />

                  <select
                    name="priority"
                    className="form-select mb-3"
                    value={form.priority}
                    onChange={handleFormChange}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>

                  <textarea
                    rows="4"
                    name="recommendation_text"
                    className="form-control"
                    value={form.recommendation_text}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() =>
                      setSelectedRecommendation(null)
                    }
                  >
                    Close
                  </button>

                  <button
                    className="btn btn-success"
                    onClick={updateRecommendation}
                  >
                    Save Changes
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default AdminRecommendations;